/*
 * Copyright (c) 2023 Puratos. All rights reserved.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { hasRole, isLoggedIn, ROLES } from '../../../data/user.model';
import {
    addToCartAction,
    fetchingMultiSKUsAction,
    isLoadingById,
    isLoadingByScope,
} from '../../../selectors/loader';
import {
    addProductsToCart,
    fetchMultiProductSKUs,
} from '../../../actions/products';
import { ProductModal } from '../../molecules/product-modal/ProductModal';
import ModalProductRow from '../products/ModalProductRow';
import { Divider } from '../../atoms/layout/Divider';
import { ModalManager } from '../../components/ModalManager';
import { getProductsSKUs } from '../../../selectors/products';
import { AddToCartButton } from '../../atoms/bootstrap/Button';
import * as $ from 'jquery';
import { GTMContextProvider } from '../../../analytics/ga4/utils/GTMContext';
import { getSkuWithNumber } from '../../../selectors/productPage';
import { addProductClickEvent } from '../../../utils/customEventHandler';
import {
    selectMyPuratosAccountEmail,
    selectUserId,
} from '../../../selectors/auth';
import { setProductCrossSell } from '../../../actions/analytics';
import { fetchBundles } from '../../../actions/my-puratos/promotions.action';
import {
    interactionPageMapper,
    interactionCrossSellMapper
} from '../../../analytics/ga4/utils/mapper';
import {getCurrencyCode, getSubTotalPrice} from '../../../selectors/cart';
import { googleEvents } from '../../../utils/googleEvents';
import { getLanguageIso } from '../../../selectors/generic';
import { GA4LoginType } from '../../../analytics/ga4/types/login';
import {
    productEventParseAdd,
    productEventParseSelect,
} from '../../../analytics/ga4/parsers/item';
import { GA4PageType } from '../../../analytics/ga4/types/page';

export const MultiProductOrderingModalId = 'multi-product-ordering';

export function MultiProductOrderingModal(props) {
    const loading = useSelector((state) =>
        isLoadingById(state, fetchingMultiSKUsAction())
    );
    const skus = useSelector((state) =>
        getProductsSKUs(state, props.payload.productIds)
    );
    const userId = useSelector(selectUserId);
    const currency = useSelector(getCurrencyCode);
    const userEmail = useSelector(selectMyPuratosAccountEmail);
    const subTotalPrice = useSelector(getSubTotalPrice);
    const preselectSkus = props.payload.skus;
    const translate = props.payload.translate;

    const [selectedState, setSelectedState] = useState([]);
    const [quantityState, setQuantityState] = useState([]);
    const [isCompleted, setIsCompleted] = useState(false);
    const [buttonDisabled, setButtonDisabled] = useState(false);

    const dispatch = useDispatch();

    const cartLoading = useSelector((state) =>
        isLoadingByScope(state, addToCartAction())
    );

    useEffect(() => {
        if (skus && skus.length) {
            const orderableProducts = []
                .concat(...skus)
                .filter(
                    (sku) =>
                        !sku.isObsolete &&
                        sku.hasPrice &&
                        sku.stockType !== 'BLOCKSALE'
                );
            setButtonDisabled(!orderableProducts.length);

            if (
                (!preselectSkus || preselectSkus?.length === 0) &&
                selectedState.length === 0
            ) {
                setSelectedState(
                    skus.map((products) => products[0]?.materialNumber)
                );
                setQuantityState(Array.from({ length: skus.length }, () => 1));
            }
        }
    }, [skus]);

    useEffect(() => {
        if (hasRole(ROLES.valid)) {
            dispatch(fetchMultiProductSKUs(props.payload.productIds));
            props.payload.showBundles &&
                dispatch(fetchBundles(props.payload.productIds));
        }
    }, [props.payload.productIds]);

    useEffect(() => {
        if (preselectSkus) {
            setSelectedState(preselectSkus);
            setQuantityState(
                Array.from({ length: preselectSkus.length }, () => 1)
            );
        }
    }, [preselectSkus]);

    useEffect(() => {
        const body = $('body');
        body.addClass('modal-open');
        return () => body.removeClass('modal-open');
    });

    const skuChangeHandler = (index) => (next) => {
        let stateCopy = [...selectedState];
        stateCopy[index] = getSkuWithNumber(skus[index], next)?.materialNumber;
        setSelectedState(stateCopy);
    };

    const onQuantityChange = useCallback(
        (index) => (quantity) => {
            let stateCopy = [...quantityState];
            stateCopy[index] = quantity;
            setQuantityState(stateCopy);
        },
        [quantityState]
    );

    const addToCartCallback = (products) => {
        setQuantityState(Array.from({ length: skus.length }, () => 1));
        setIsCompleted(true);

        products
            .filter(item => item.hasPrice)
            .map((item) => {
                dispatch(
                    setProductCrossSell({
                        ...item,
                        type: interactionCrossSellMapper(props.payload.type),
                    })
                )

                const promotionData = {
                    promotion_id: item.promoCode ? item.promoCode : '',
                    promotion_name: item.promoText ? item.promoText : '',
                };

                googleEvents.addToCart({
                    user_id: userId,
                    page_language: getLanguageIso(),
                    email_address: isLoggedIn() ? userEmail : '',
                    login_status: userId
                        ? GA4LoginType.LOGGED_IN
                        : GA4LoginType.GUEST,
                    website_section: interactionPageMapper(props.payload.type),
                    subTotalPrice: subTotalPrice,
                    currency: currency,
                    items: productEventParseAdd(
                        item,
                        currency,
                        interactionPageMapper(props.payload.type),
                        item.quantity,
                        promotionData
                    ),
                });
            });
    };

    const dispatchAddToCart = () => {
        if (!loading) {
            setIsCompleted(false);

            const selectedSkus = skus
                .map((product) =>
                    product.find((sku) =>
                        selectedState.includes(sku?.materialNumber)
                    )
                )
                .filter((sku) => sku?.stockType !== 'BLOCKSALE')
                .map((sku, index) => ({
                    ...sku,
                    quantity: quantityState[index],
                }));

            selectedSkus?.length >= 1 &&
                dispatch(
                    addProductsToCart(selectedSkus, props.payload.type, () =>
                        addToCartCallback(selectedSkus)
                    )
                );
        }
    };

    const addEvent = (product, position) => {
        dispatch(
            setProductCrossSell({
                ...product,
                type: interactionCrossSellMapper(props.payload.type),
            })
        );
        addProductClickEvent(
            product,
            props.payload.type,
            true,
            userId,
            currency,
            position
        );
        googleEvents.selectItem({
            user_id: userId,
            page_language: getLanguageIso(),
            email_address: isLoggedIn() ? userEmail : '',
            login_status: userId ? GA4LoginType.LOGGED_IN : GA4LoginType.GUEST,
            website_section: GA4PageType.recipe,
            items: productEventParseSelect(
                product,
                currency,
                props.payload.type
            ),
        });
    };
// TODO make ModalProductRow use tagData
    return (
        <ProductModal
            skus={skus}
            loading={loading}
            title={'Select package'}
            addAll={props.payload.addAll}
        >
            {skus?.map((sku, index) => (
                <React.Fragment key={index}>
                    <GTMContextProvider type={props.payload.type}>
                        <ModalProductRow
                            sku={sku}
                            skuChangeHandler={skuChangeHandler(index)}
                            onQuantityChange={onQuantityChange(index)}
                            addAll={props.payload.addAll}
                            showBundles={props.payload.showBundles || false}
                            hideCartAmountAlert={
                                props.payload.hideCartAmountAlert
                            }
                            selected={sku
                                .map((item) => item.materialNumber)
                                .find((number) =>
                                    selectedState.includes(number)
                                )}
                            quantity={quantityState[index]}
                            addEvent={addEvent}
                            position={index + 1}
                        />
                        {index < skus.length - 1 && <Divider />}
                    </GTMContextProvider>
                </React.Fragment>
            ))}

            {props.payload.addAll && (
                <AddToCartButton
                    className={'btn-add-all'}
                    loading={cartLoading}
                    disabled={cartLoading || buttonDisabled}
                    onClick={dispatchAddToCart}
                    response={isCompleted}
                    translate={translate}
                    icon
                    color={'PRIMARY'}
                    text={translate('Add all Puratos ingredients')}
                />
            )}
        </ProductModal>
    );
}

ModalManager.modals[MultiProductOrderingModalId] = MultiProductOrderingModal;
